import { create } from 'zustand';
import { mappingApi, MappingTemplate } from '../services/api/mapping';

interface MappingsState {
  // Data
  mappings: MappingTemplate[];
  selectedMapping: MappingTemplate | null;

  // Pagination
  currentOffset: number;
  pageSize: number;
  hasMore: boolean;
  total: number;

  // UI State
  loading: boolean;
  error: string | null;

  // Actions
  fetchMappings: (params?: {
    limit?: number;
    offset?: number;
    name?: string;
    key?: string;
    labels?: string;
    docType?: string;
    status?: string;
    creator?: string;
    owner?: string;
  }) => Promise<void>;
  fetchMoreMappings: () => Promise<void>;
  fetchMappingById: (key: string) => Promise<void>;
  createMapping: (file: File, metadata: { name: string; type: 'PDF' | 'EXCEL' | 'HTML' }) => Promise<MappingTemplate>;
  updateMapping: (key: string, data: { name?: string; status?: 'NEW' | 'AUTO_MAPPING' | 'IN_PROGRESS' | 'COMPLETED' | 'IN_VERIFICATION' | 'VERIFIED'; metadata?: any }) => Promise<void>;
  updateMappingStatus: (key: string, status: string) => void;
  deleteMapping: (key: string) => Promise<void>;
  setSelectedMapping: (mapping: MappingTemplate | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  mappings: [],
  selectedMapping: null,
  currentOffset: 0,
  pageSize: 100,
  hasMore: false,
  total: 0,
  loading: false,
  error: null,
};

export const useMappingsStore = create<MappingsState>((set, get) => ({
  ...initialState,

  fetchMappings: async (params) => {
    set({ loading: true, error: null });
    try {
      const pageSize = params?.limit || get().pageSize;
      const offset = params?.offset || 0;

      // Use PDF mapper list endpoint for PDFs
      const response = await mappingApi.listPdf({
        max: pageSize,
        offset,
        name: params?.name,
        key: params?.key,
        labels: params?.labels,
        docType: params?.docType,
        status: params?.status,
        creator: params?.creator,
        owner: params?.owner,
        sort: 'lastUpdated',
        order: 'desc',
      });

      const { data, pagination } = response.data;

      // Transform PdfMapping to MappingTemplate format
      const mappings: MappingTemplate[] = data.map((pdfMapping: any) => ({
        key: pdfMapping.key,
        name: pdfMapping.name || 'Untitled',
        type: pdfMapping.docType || 'po',
        fileType: 'PDF',
        status: pdfMapping.status || 'NEW',
        tenantId: pdfMapping.tenantId || '',
        labels: pdfMapping.labels || [],
        createdAt: pdfMapping.dateCreated || new Date().toISOString(),
        updatedAt: pdfMapping.lastUpdated || new Date().toISOString(),
        createdBy: pdfMapping.creator,
        updatedBy: pdfMapping.lastUpdatedBy,
      }));

      set({
        mappings,
        currentOffset: pagination?.offset || 0,
        pageSize: pagination?.max || pageSize,
        hasMore: (pagination?.offset || 0) + data.length < (pagination?.total || 0),
        total: pagination?.total || data.length,
        loading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch mappings:', error);
      set({
        error: error.response?.data?.message || error.message || 'Failed to fetch mappings',
        loading: false,
      });
    }
  },

  fetchMoreMappings: async () => {
    const { loading, hasMore, currentOffset, pageSize, mappings } = get();

    if (loading || !hasMore) {
      return;
    }

    const nextOffset = currentOffset + pageSize;

    set({ loading: true, error: null });
    try {
      const response = await mappingApi.listPdf({
        max: pageSize,
        offset: nextOffset,
        sort: 'lastUpdated',
        order: 'desc',
      });

      const { data, pagination } = response.data;

      // Transform and append new mappings
      const newMappings: MappingTemplate[] = data.map((pdfMapping: any) => ({
        key: pdfMapping.key,
        name: pdfMapping.name || 'Untitled',
        type: pdfMapping.docType || 'po',
        fileType: 'PDF',
        status: pdfMapping.status || 'NEW',
        tenantId: pdfMapping.tenantId || '',
        labels: pdfMapping.labels || [],
        createdAt: pdfMapping.dateCreated || new Date().toISOString(),
        updatedAt: pdfMapping.lastUpdated || new Date().toISOString(),
        createdBy: pdfMapping.creator,
        updatedBy: pdfMapping.lastUpdatedBy,
      }));

      const { total } = get();
      set({
        mappings: [...mappings, ...newMappings],
        currentOffset: pagination?.offset || nextOffset,
        hasMore: (pagination?.offset || nextOffset) + newMappings.length < (pagination?.total || total),
        loading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch more mappings:', error);
      set({
        error: error.response?.data?.message || error.message || 'Failed to fetch more mappings',
        loading: false,
      });
    }
  },

  fetchMappingById: async (key: string) => {
    set({ loading: true, error: null });
    try {
      const response = await mappingApi.get(key);
      set({
        selectedMapping: response.data.data,
        loading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch mapping:', error);
      set({
        error: error.response?.data?.message || error.message || 'Failed to fetch mapping',
        loading: false,
      });
    }
  },

  createMapping: async (file: File, metadata) => {
    set({ loading: true, error: null });
    try {
      const response = await mappingApi.create({
        file,
        name: metadata.name,
        type: metadata.type,
      });

      const newMapping = response.data.data;

      set((state) => ({
        mappings: [newMapping, ...state.mappings],
        selectedMapping: newMapping,
        loading: false,
      }));

      return newMapping;
    } catch (error: any) {
      console.error('Failed to create mapping:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create mapping';
      set({
        error: errorMessage,
        loading: false,
      });
      throw new Error(errorMessage);
    }
  },

  updateMapping: async (key: string, data) => {
    set({ loading: true, error: null });
    try {
      const response = await mappingApi.update(key, data);
      const updatedMapping = response.data.data;

      set((state) => ({
        mappings: state.mappings.map((m) =>
          m.key === key ? updatedMapping : m
        ),
        selectedMapping: state.selectedMapping?.key === key ? updatedMapping : state.selectedMapping,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Failed to update mapping:', error);
      set({
        error: error.response?.data?.message || error.message || 'Failed to update mapping',
        loading: false,
      });
      throw error;
    }
  },

  updateMappingStatus: (key: string, status: string) => {
    set((state) => ({
      mappings: state.mappings.map((m) =>
        m.key === key ? { ...m, status: status as any } : m
      ),
      selectedMapping: state.selectedMapping?.key === key
        ? { ...state.selectedMapping, status: status as any }
        : state.selectedMapping,
    }));
  },

  deleteMapping: async (key: string) => {
    set({ loading: true, error: null });
    try {
      await mappingApi.delete(key);

      set((state) => ({
        mappings: state.mappings.filter((m) => m.key !== key),
        selectedMapping: state.selectedMapping?.key === key ? null : state.selectedMapping,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Failed to delete mapping:', error);
      set({
        error: error.response?.data?.message || error.message || 'Failed to delete mapping',
        loading: false,
      });
      throw error;
    }
  },

  setSelectedMapping: (mapping) => {
    set({ selectedMapping: mapping });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
}));
